<template>
  <b-modal
    v-model="visible"
    title="Call Outcome"
    size="md"
    centered
    hide-footer
    @hidden="onHidden"
  >
    <p class="text-success mb-1">
      <feather-icon icon="CheckCircleIcon" class="mr-50" />
      Call initiated to <strong>{{ displayTarget }}</strong>.
      <span class="d-block small text-muted mt-25">Answer on your phone, then confirm the outcome below.</span>
    </p>

    <b-form-group label="Did the call connect?" class="mb-1">
      <div class="d-flex flex-wrap" style="gap:8px;">
        <b-button
          :variant="connected === true ? 'success' : 'outline-success'"
          size="sm"
          @click="connected = true"
        >
          <feather-icon icon="PhoneCallIcon" class="mr-25" /> Yes, Connected
        </b-button>
        <b-button
          :variant="connected === false ? 'danger' : 'outline-danger'"
          size="sm"
          @click="connected = false"
        >
          <feather-icon icon="PhoneOffIcon" class="mr-25" /> Not Answered
        </b-button>
      </div>
    </b-form-group>

    <!-- Connected + candidate in ATS job pipeline -->
    <div v-if="connected === true && hasCandidateJob" class="mb-1">
      <b-alert v-if="!isFollowUpCall && selectedOutcomeBlocked" show variant="warning" class="py-75 px-1 mb-1">
        <feather-icon icon="LockIcon" class="mr-25" />
        <span class="small">{{ lockedStatusMessage }} You can still add call notes without changing pipeline status.</span>
      </b-alert>
      <b-form-group v-if="!isFollowUpCall" label="Update candidate status (optional)" label-for="post-dial-status">
        <b-form-select
          id="post-dial-status"
          v-model="postCallStatus"
          :options="statusOptions"
          @change="onStatusChange"
        />
      </b-form-group>

      <!-- Interested → same as Dialer: redirect to fill details & share profile -->
      <div v-if="!isFollowUpCall && selectedOutcomeAllowed && postCallStatus === 'Interested'" class="call-outcome-interested mt-1">
        <div class="d-flex align-items-center mb-50">
          <feather-icon icon="ZapIcon" size="14" class="mr-50 text-success" />
          <span class="small font-weight-bold text-success">Candidate is Interested!</span>
        </div>
        <p class="small text-muted mb-75">
          Fill in the candidate's details (CTC, experience, notice period, etc.) and share the profile with the client.
        </p>
        <div class="d-flex justify-content-end">
          <b-button size="sm" variant="success" @click="goToEditAndShare">
            <feather-icon icon="ExternalLinkIcon" size="12" class="mr-50" />
            Fill Details &amp; Share Profile
          </b-button>
        </div>
      </div>

      <!-- Duplicate → reason + cooling period -->
      <div v-if="!isFollowUpCall && selectedOutcomeAllowed && postCallStatus === 'Duplicate'" class="mt-1">
        <b-form-group label-for="dup-reason-select" class="mb-75">
          <template #label>
            Duplicate Reason <span class="text-danger">*</span>
          </template>
          <b-form-select
            id="dup-reason-select"
            v-model="dupReasonSelected"
            :options="duplicateSelectOptions"
            :state="dupReasonTouched && !dupReasonSelected ? false : null"
            @change="dupReasonTouched = true"
          />
          <small v-if="dupReasonTouched && !dupReasonSelected" class="text-danger">Please select a duplicate reason.</small>
        </b-form-group>
        <b-form-group label-for="cooling-timeline-select" class="mb-75">
          <template #label>
            Cooling Period <span class="text-danger">*</span>
          </template>
          <b-form-select
            id="cooling-timeline-select"
            v-model="coolingTimeline"
            :options="coolingTimelineSelectOptions"
            :state="coolingTimelineTouched && coolingTimeline == null ? false : null"
            @change="coolingTimelineTouched = true"
          />
          <small v-if="coolingTimelineTouched && coolingTimeline == null" class="text-danger">Please select a cooling period.</small>
        </b-form-group>
        <p v-if="computedDuplicateDate" class="mb-0 small text-muted">
          Duplicate date: {{ computedDuplicateDate }}
        </p>
      </div>

      <!-- Not Interested → reason dropdown -->
      <div v-if="!isFollowUpCall && selectedOutcomeAllowed && postCallStatus === 'Not Interested'" class="mt-1">
        <b-form-group label-for="ni-reason-select" class="mb-75">
          <template #label>
            Not Interested Reason <span class="text-danger">*</span>
          </template>
          <b-form-select
            id="ni-reason-select"
            v-model="notInterestedReasonSelected"
            :options="notInterestedReasonSelectOptions"
            :state="notInterestedReasonTouched && !notInterestedReasonSelected ? false : null"
            @change="notInterestedReasonTouched = true"
          />
          <small v-if="notInterestedReasonTouched && !notInterestedReasonSelected" class="text-danger">Please select a not interested reason.</small>
        </b-form-group>
        <b-form-group label="Comment (optional)" label-for="ni-comment">
          <b-form-textarea
            id="ni-comment"
            v-model="statusComment"
            rows="2"
            placeholder="Add any additional notes…"
          />
        </b-form-group>
      </div>

      <!-- Not Relevant → comment required -->
      <div v-if="!isFollowUpCall && selectedOutcomeAllowed && postCallStatus === 'Not Relevant'" class="mt-1">
        <b-form-group label-for="nr-comment">
          <template #label>
            Why is this candidate not relevant? <span class="text-danger">*</span>
          </template>
          <b-form-textarea
            id="nr-comment"
            v-model="statusComment"
            rows="3"
            placeholder="Add comment for not relevant candidate…"
            :state="commentTouched && !statusComment.trim() ? false : null"
            @input="commentTouched = true"
          />
          <small v-if="commentTouched && !statusComment.trim()" class="text-danger">Please add a comment.</small>
        </b-form-group>
      </div>

      <!-- Call Back / Not Answered / etc. when selected from dropdown while connected -->
      <div v-if="!isFollowUpCall && selectedOutcomeAllowed && ['Call Back', 'Not Answered', 'Not Reachable', 'Wrong Number'].includes(postCallStatus)" class="mt-1">
        <b-form-group label="Comment (optional)" label-for="status-comment">
          <b-form-textarea
            id="status-comment"
            v-model="statusComment"
            rows="2"
            placeholder="Add any notes about this outcome…"
          />
        </b-form-group>
      </div>
      <b-form-group v-else-if="isFollowUpCall || selectedOutcomeBlocked || statusWriteVerdict.reason === 'same'" label="Call notes (optional)" label-for="locked-status-comment">
        <b-form-textarea
          id="locked-status-comment"
          v-model="statusComment"
          rows="2"
          placeholder="Add notes without changing pipeline status…"
        />
      </b-form-group>
    </div>

    <b-alert v-if="connected === true && !hasCandidateJob" show variant="light" class="py-75 px-1 mb-1">
      <feather-icon icon="InfoIcon" class="mr-25" />
      <span class="small">Candidate not linked to this job. You can add them to ATS from the row menu after closing.</span>
    </b-alert>

    <!-- Not connected + candidate in ATS → offer Not Answered status update -->
    <div v-if="connected === false && hasCandidateJob" class="mb-1">
      <b-alert v-if="isFollowUpCall" show variant="light" class="py-75 px-1 mb-75">
        <feather-icon icon="InfoIcon" class="mr-25" />
        <span class="small">This call attempt is recorded in your call log automatically.</span>
      </b-alert>
      <b-alert v-else-if="!notAnsweredVerdict.changeStatus && currentStatus" show variant="warning" class="py-75 px-1 mb-75">
        <feather-icon icon="LockIcon" class="mr-25" />
        <span class="small">{{ notAnsweredVerdict.message || lockedStatusMessage }} This attempt is still recorded in the call log.</span>
      </b-alert>
      <b-alert v-else show variant="light" class="py-75 px-1 mb-75">
        <feather-icon icon="InfoIcon" class="mr-25" />
        <span class="small">This call attempt is recorded in your call log automatically. You can also update the candidate status to <strong>Not Answered</strong>.</span>
      </b-alert>
      <b-form-group label="Comment (optional)" label-for="na-comment">
        <b-form-textarea
          id="na-comment"
          v-model="statusComment"
          rows="2"
          placeholder="Add any notes about this attempt…"
        />
      </b-form-group>
    </div>

    <!-- Not connected + not in ATS → call log only (matches Dialer) -->
    <b-alert v-if="connected === false && !hasCandidateJob" show variant="light" class="py-75 px-1 mb-1">
      <feather-icon icon="InfoIcon" class="mr-25" />
      <span class="small">This attempt is recorded in your call log automatically. No ATS status to update — candidate is not linked to a job.</span>
    </b-alert>

    <b-alert v-if="validationError" show variant="danger" class="py-50 px-1 mb-1">
      <small>{{ validationError }}</small>
    </b-alert>

    <div class="d-flex justify-content-end flex-wrap mt-1" style="gap:8px;">
      <b-button variant="outline-secondary" size="sm" @click="visible = false">
        Close
      </b-button>

      <template v-if="connected === false && hasCandidateJob">
        <b-button variant="outline-secondary" size="sm" @click="confirmOutcome">
          Done (log only)
        </b-button>
        <b-button
          v-if="!isFollowUpCall && notAnsweredVerdict.changeStatus"
          variant="primary"
          size="sm"
          :disabled="saving"
          @click="saveNotAnswered"
        >
          <b-spinner v-if="saving" small class="mr-50" />
          Update to Not Answered
        </b-button>
        <b-button
          v-else-if="statusComment.trim()"
          variant="primary"
          size="sm"
          :disabled="saving"
          @click="saveNotesOnly"
        >
          <b-spinner v-if="saving" small class="mr-50" />
          Save notes
        </b-button>
      </template>

      <b-button
        v-else-if="connected === true && hasCandidateJob && isFollowUpCall"
        variant="primary"
        size="sm"
        :disabled="saving"
        @click="saveNotesOnly"
      >
        <b-spinner v-if="saving" small class="mr-50" />
        Save notes &amp; Close
      </b-button>

      <b-button
        v-else-if="connected === true && hasCandidateJob && (selectedOutcomeBlocked || statusWriteVerdict.reason === 'same') && statusComment.trim()"
        variant="primary"
        size="sm"
        :disabled="saving"
        @click="saveNotesOnly"
      >
        <b-spinner v-if="saving" small class="mr-50" />
        Save notes
      </b-button>

      <b-button
        v-else-if="connected === true && hasCandidateJob && !isFollowUpCall && selectedOutcomeAllowed && postCallStatus && postCallStatus !== 'Interested'"
        variant="primary"
        size="sm"
        :disabled="saving"
        @click="saveStatus"
      >
        <b-spinner v-if="saving" small class="mr-50" />
        Save &amp; Close
      </b-button>

      <b-button
        v-else-if="connected !== null"
        :variant="connected ? 'success' : 'secondary'"
        size="sm"
        @click="confirmOutcome"
      >
        Done
      </b-button>
    </div>
  </b-modal>
</template>

<script>
import {
  BModal,
  BButton,
  BFormGroup,
  BFormSelect,
  BFormTextarea,
  BAlert,
  BSpinner,
} from 'bootstrap-vue'
import {
  POST_CALL_STATUS_OPTIONS,
  NOT_INTERESTED_REASON_OPTIONS,
  DUPLICATE_REASON_OPTIONS,
  COOLING_TIMELINE_OPTIONS,
  computeDuplicateDateFromCooling,
  evaluateCallSurfaceStatusWrite,
  validateCallOutcomeForm,
} from '@/utils/call-outcome-helpers'
import { isFollowUpPipelineStatus, statusFlowContextFromJob } from '@/utils/candidate-status-flow'

export default {
  name: 'CallPostDialModal',

  components: {
    BModal,
    BButton,
    BFormGroup,
    BFormSelect,
    BFormTextarea,
    BAlert,
    BSpinner,
  },

  props: {
    show: { type: Boolean, default: false },
    phone: { type: String, default: '' },
    candidateName: { type: String, default: '' },
    candidateId: { type: [String, Number], default: null },
    jobId: { type: [String, Number], default: null },
    jobCategory: { type: String, default: '' },
    jobVertical: { type: String, default: '' },
    hasCandidateJob: { type: Boolean, default: false },
    currentStatus: { type: String, default: '' },
    statusFlowContext: { type: Object, default: () => ({}) },
    saving: { type: Boolean, default: false },
  },

  data() {
    return {
      connected: null,
      postCallStatus: '',
      statusOptions: POST_CALL_STATUS_OPTIONS,
      notInterestedReasonSelected: null,
      notInterestedReasonTouched: false,
      notInterestedReasonSelectOptions: NOT_INTERESTED_REASON_OPTIONS,
      dupReasonSelected: null,
      coolingTimeline: null,
      dupReasonTouched: false,
      coolingTimelineTouched: false,
      duplicateSelectOptions: DUPLICATE_REASON_OPTIONS,
      coolingTimelineSelectOptions: COOLING_TIMELINE_OPTIONS,
      statusComment: '',
      commentTouched: false,
      validationError: '',
    }
  },

  computed: {
    visible: {
      get() { return this.show },
      set(v) { this.$emit('update:show', v) },
    },
    displayTarget() {
      if (this.candidateName && this.candidateName !== '—') {
        return `${this.candidateName} (${this.phone})`
      }
      return this.phone || '—'
    },
    computedDuplicateDate() {
      return computeDuplicateDateFromCooling(this.coolingTimeline)
    },
    resolvedFlowContext() {
      return statusFlowContextFromJob(null, {
        ...this.statusFlowContext,
        category: this.jobCategory || this.statusFlowContext.category,
        vertical: this.jobVertical || this.statusFlowContext.vertical,
      })
    },
    statusWriteVerdict() {
      return evaluateCallSurfaceStatusWrite(
        this.currentStatus,
        this.postCallStatus,
        this.resolvedFlowContext,
      )
    },
    notAnsweredVerdict() {
      return evaluateCallSurfaceStatusWrite(
        this.currentStatus,
        'Not Answered',
        this.resolvedFlowContext,
      )
    },
    selectedOutcomeBlocked() {
      return this.statusWriteVerdict.reason === 'blocked'
    },
    selectedOutcomeAllowed() {
      return this.statusWriteVerdict.changeStatus
    },
    isFollowUpCall() {
      return isFollowUpPipelineStatus(this.currentStatus)
    },
    pipelineStatusLocked() {
      return this.selectedOutcomeBlocked
    },
    lockedStatusMessage() {
      return this.statusWriteVerdict.message
        || `Cannot change status from ${this.currentStatus || 'this status'} (status flow).`
    },
  },

  watch: {
    show(val) {
      if (val) this.resetForm()
    },
  },

  methods: {
    resetForm() {
      this.connected = null
      this.postCallStatus = ''
      this.notInterestedReasonSelected = null
      this.notInterestedReasonTouched = false
      this.dupReasonSelected = null
      this.coolingTimeline = null
      this.dupReasonTouched = false
      this.coolingTimelineTouched = false
      this.statusComment = ''
      this.commentTouched = false
      this.validationError = ''
    },

    onStatusChange() {
      this.notInterestedReasonSelected = null
      this.notInterestedReasonTouched = false
      this.dupReasonSelected = null
      this.coolingTimeline = null
      this.dupReasonTouched = false
      this.coolingTimelineTouched = false
      this.statusComment = ''
      this.commentTouched = false
      this.validationError = ''
    },

    buildFormPayload() {
      return {
        connected: this.connected,
        postCallStatus: this.postCallStatus || null,
        notInterestedReason: this.notInterestedReasonSelected,
        duplicateReason: this.dupReasonSelected,
        coolingTimeline: this.coolingTimeline,
        comment: this.statusComment,
      }
    },

    validateAndEmit(saveStatus, overrideStatus = null) {
      const postCallStatus = overrideStatus || this.postCallStatus
      const validation = validateCallOutcomeForm({
        connected: this.connected,
        postCallStatus,
        notInterestedReason: this.notInterestedReasonSelected,
        duplicateReason: this.dupReasonSelected,
        coolingTimeline: this.coolingTimeline,
        comment: this.statusComment,
      })

      if (!validation.valid) {
        this.validationError = validation.message
        if (postCallStatus === 'Not Interested') this.notInterestedReasonTouched = true
        if (postCallStatus === 'Duplicate') {
          this.dupReasonTouched = true
          this.coolingTimelineTouched = true
        }
        if (postCallStatus === 'Not Relevant') this.commentTouched = true
        return false
      }

      this.validationError = ''
      this.$emit('outcome', {
        connected: this.connected,
        postCallStatus,
        saveStatus,
        ...this.buildFormPayload(),
      })
      this.visible = false
      return true
    },

    confirmOutcome() {
      this.$emit('outcome', {
        connected: this.connected,
        postCallStatus: this.postCallStatus || null,
        saveStatus: false,
        ...this.buildFormPayload(),
      })
      this.visible = false
    },

    saveStatus() {
      if (this.isFollowUpCall || !this.selectedOutcomeAllowed) {
        this.saveNotesOnly()
        return
      }
      this.validateAndEmit(true)
    },

    saveNotAnswered() {
      if (this.isFollowUpCall || !this.notAnsweredVerdict.changeStatus) {
        this.saveNotesOnly()
        return
      }
      this.validateAndEmit(true, 'Not Answered')
    },

    saveNotesOnly() {
      this.$emit('outcome', {
        connected: this.connected,
        postCallStatus: null,
        saveStatus: false,
        notesOnly: true,
        ...this.buildFormPayload(),
      })
      this.visible = false
    },

    goToEditAndShare() {
      if (!this.candidateId) return
      this.$emit('outcome', {
        connected: true,
        postCallStatus: 'Interested',
        saveStatus: false,
        interested: true,
        ...this.buildFormPayload(),
      })
      this.visible = false
      this.$router.push({
        name: 'apps-candidates-edit',
        params: { id: this.candidateId, jobId: this.jobId || '' },
        query: {
          interested: 'YES',
          ...(this.jobCategory && { category: this.jobCategory }),
          ...(this.jobVertical && { vertical: this.jobVertical }),
        },
      })
    },

    onHidden() {
      if (this.connected === null) {
        this.$emit('dismissed')
      }
      this.resetForm()
    },
  },
}
</script>

<style scoped>
.call-outcome-interested {
  background: #f6fff8;
  border: 1px solid #d4edda;
  border-radius: 0.4rem;
  padding: 0.75rem;
}
</style>
